import { useEffect, useState, type RefObject } from "react";
import { useInView, useReducedMotion } from "framer-motion";

/**
 * Motion helpers for the homepage only. Timings mirror the approved V6 design: a 60ms
 * tick for typed text, 45 ticks of hold, and a 6.6s step for the reflection demo.
 */

export const TYPE_TICK_MS = 60;
export const TYPE_HOLD_TICKS = 45;

/** True when the visitor asked for reduced motion (framer-motion's hook, already in the app). */
export function useLandingReducedMotion(): boolean {
  return useReducedMotion() ?? false;
}

/** Tracks document.visibilityState so timers can pause while the tab is hidden. */
export function usePageVisible(): boolean {
  const [visible, setVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden",
  );
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return visible;
}

/** Whether an element is on screen (not once: used to pause and resume ambient motion). */
export function useOnScreen(ref: RefObject<Element | null>, amount = 0.15): boolean {
  return useInView(ref, { amount });
}

/** Fires once when an element first enters the viewport (entrance / settle animations). */
export function useEnteredView(ref: RefObject<Element | null>, amount = 0.25): boolean {
  return useInView(ref, { amount, once: true });
}

/**
 * Pointer parallax from the V6 reference: while a fine pointer moves over the section,
 * set --px/--py (-0.5..0.5) on it; `.lp-layer` children shift by `--d` px (landing.css).
 * requestAnimationFrame-throttled; skipped on touch, small screens and reduced motion.
 */
export function usePointerParallax(ref: RefObject<HTMLElement | null>, enabled = true) {
  const reduce = useLandingReducedMotion();
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || reduce) return;
    const capable = window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 1024px)");
    let frame = 0;
    let last: PointerEvent | null = null;

    const apply = () => {
      frame = 0;
      if (!last) return;
      const r = el.getBoundingClientRect();
      el.style.setProperty("--px", ((last.clientX - r.left) / r.width - 0.5).toFixed(3));
      el.style.setProperty("--py", ((last.clientY - r.top) / r.height - 0.5).toFixed(3));
    };
    const onMove = (e: PointerEvent) => {
      if (!capable.matches || e.pointerType !== "mouse") return;
      last = e;
      if (!frame) frame = requestAnimationFrame(apply);
    };
    const onLeave = () => {
      last = null;
      el.style.setProperty("--px", "0");
      el.style.setProperty("--py", "0");
    };

    el.addEventListener("pointermove", onMove, { passive: true });
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
      if (frame) cancelAnimationFrame(frame);
      onLeave();
    };
  }, [ref, enabled, reduce]);
}
